import React,{useState,useRef,useCallback,useEffect} from 'react';
import {Flag,useStickyEdgeOverflow} from '../components/Shared';
import {api} from '../lib/api';
import {CURRENCIES,PAYMENT_AMOUNT_TYPES,PAYMENT_FREQUENCIES,PAYMENT_STATUSES} from '../lib/constants';
import {sendPushNotification} from '../lib/pushNotifications';
import {createPaymentCheckout} from '../lib/stripeConnect';
import {currencyForCountry,currencySymbol,downloadCSV,fmtDateShort,freqLabel,perInstallmentAmount,statusInfo,toISODate,today} from '../lib/utils';

// Keeps the payments table's columns in date order — including right after a coach adds
// a one-off "extra" period, which otherwise always lands at the end regardless of the
// date they gave it. Periods with no date at all sort to the end, after every dated one.
function sortPeriods(arr){
  return [...(arr||[])].sort((a,b)=>{
    if(!a.due_date&&!b.due_date)return 0;
    if(!a.due_date)return 1;
    if(!b.due_date)return -1;
    return new Date(a.due_date)-new Date(b.due_date);
  });
}

export function PaymentPlanSetup({frequencyDefault,orgCountry,onCreate,ac,AS,onCancel}){
  const [amount,setAmount]=useState("");
  const [amountType,setAmountType]=useState("per_period");
  const [currency,setCurrency]=useState(()=>currencyForCountry(orgCountry));
  const [frequency,setFrequency]=useState(frequencyDefault||"monthly");
  const [numPeriods,setNumPeriods]=useState(frequency==="per_season"?1:10);
  const [startDate,setStartDate]=useState(()=>toISODate(new Date()));
  const I={width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,boxSizing:"border-box"};
  const L={fontSize:10,fontWeight:600,color:"var(--c-muted)",display:"block",marginBottom:4};
  const sym=currencySymbol(currency);
  const perInstallment=amount&&numPeriods>0&&amountType==="total"?(parseFloat(amount)/numPeriods):null;
  return(
    <div style={{background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`,padding:18,maxWidth:420}}>
      <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Set Up Payment Plan</div>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <label style={L}>Currency{orgCountry&&currency===currencyForCountry(orgCountry)?` (set from your organisation's country — ${orgCountry})`:""}</label>
          <select value={currency} onChange={e=>setCurrency(e.target.value)} style={I}>
            {CURRENCIES.map(c=><option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label style={L}>Frequency</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {PAYMENT_FREQUENCIES.map(f=>(
              <button key={f.key} onClick={()=>{setFrequency(f.key);if(f.key==="per_season")setNumPeriods(1);else if(numPeriods===1)setNumPeriods(f.key==="custom"?3:10);}} style={{flex:"1 1 70px",padding:"8px",borderRadius:7,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:frequency===f.key?ac:AS.bgColor,color:frequency===f.key?"#fff":"var(--c-muted)"}}>{f.label}</button>
            ))}
          </div>
        </div>
        {frequency==="custom"&&(
          <div>
            <label style={L}>How many payments are needed this season?</label>
            <input type="number" min="1" max="52" value={numPeriods} onChange={e=>setNumPeriods(parseInt(e.target.value,10)||1)} style={I}/>
            <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:4}}>We'll create that many columns — you set each payment's due date yourself once the plan is created.</div>
          </div>
        )}
        {frequency!=="per_season"&&frequency!=="custom"&&(
          <div>
            <label style={L}>Number of Periods to Create</label>
            <input type="number" min="1" max="52" value={numPeriods} onChange={e=>setNumPeriods(parseInt(e.target.value,10)||1)} style={I}/>
            <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:4}}>You can always add more periods later.</div>
          </div>
        )}
        {frequency!=="custom"&&(
          <div>
            <label style={L}>{frequency==="per_season"?"Due Date":"First Due Date"}</label>
            <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={I}/>
          </div>
        )}
        <div>
          <label style={L}>Is this amount the total for the season, or the amount per payment?</label>
          <div style={{display:"flex",gap:6}}>
            {PAYMENT_AMOUNT_TYPES.map(t=>(
              <button key={t.key} onClick={()=>setAmountType(t.key)} style={{flex:1,padding:"8px",borderRadius:7,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:amountType===t.key?ac:AS.bgColor,color:amountType===t.key?"#fff":"var(--c-muted)"}}>{t.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={L}>{amountType==="total"?"Total Amount for the Season":"Amount Due (per payment)"}</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="e.g. 25.00" style={I}/>
          {perInstallment!==null&&<div style={{fontSize:10,color:"var(--c-muted2)",marginTop:4}}>≈ {sym}{perInstallment.toFixed(2)} per payment across {numPeriods} payment{numPeriods!==1?"s":""}.</div>}
        </div>
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <button onClick={()=>onCreate({amount,amountType,currency,frequency,numPeriods:frequency==="per_season"?1:numPeriods,startDate})} disabled={!amount} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:amount?"pointer":"default",opacity:amount?1:0.5}}>Create Plan</button>
          {onCancel&&<button onClick={onCancel} style={{padding:"10px 16px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:13,cursor:"pointer"}}>Cancel</button>}
        </div>
      </div>
    </div>
  );
}

export function PaymentsPage({org,teams,allPlayers,auth,ac,AS,isMobile}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [activeTeamId,setActiveTeamId]=useState(null);
  const [plan,setPlan]=useState(undefined); // undefined=loading, null=no plan yet, object=plan
  const [periods,setPeriods]=useState([]);
  const [records,setRecords]=useState([]);
  const [editingAmount,setEditingAmount]=useState(false);
  const [amountDraft,setAmountDraft]=useState("");
  const [amountTypeDraft,setAmountTypeDraft]=useState("per_period");
  const [currencyDraft,setCurrencyDraft]=useState("GBP");
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(false);
  // The Player column is pinned to the left (position:sticky) so it's always
  // visible while payment periods scroll underneath it — hasHiddenLeft is true
  // whenever a period is currently scrolled behind that pinned column, which
  // is when the "more, scroll" dots below are shown.
  const [periodsScrollRef,hasHiddenLeft]=useStickyEdgeOverflow("left");

  const activeTeam=teams.find(t=>t.id===activeTeamId);
  const teamPlayers=activeTeam?allPlayers.filter(p=>(activeTeam.playerIds||[]).includes(p.id)).sort((a,b)=>a.name.localeCompare(b.name)):[];

  // A period's amount is normally just the plan's usual amount (or, for a
  // "total for the season" plan, that total split evenly) — but a coach can
  // override any single period with its own amount (see periodAmount below),
  // e.g. a one-off tournament fee that doesn't match the regular schedule.
  function periodAmount(per){
    if(per?.amount!=null&&per.amount!=="")return Number(per.amount);
    if(!plan)return 0;
    return plan.amount_type==="total"?perInstallmentAmount(plan,periods.length):Number(plan.amount||0);
  }

  useEffect(()=>{ if(activeTeamId)loadPlan(); },[activeTeamId]);

  async function loadPlan(){
    setPlan(undefined);setPeriods([]);setRecords([]);
    const rows=await api.get(auth.token,"payment_plans",`team_id=eq.${activeTeamId}`);
    const p=rows&&rows[0]?rows[0]:null;
    setPlan(p);
    if(p){
      const pers=await api.get(auth.token,"payment_periods",`plan_id=eq.${p.id}&order=period_index.asc`);
      setPeriods(sortPeriods(pers));
      const perIds=(pers||[]).map(x=>x.id);
      if(perIds.length>0){
        const recs=await api.get(auth.token,"payment_records",`period_id=in.(${perIds.join(",")})`);
        setRecords(recs||[]);
      }
    }
  }

  async function createPlan(fields){
    const created=await api.post(auth.token,"payment_plans",[{team_id:activeTeamId,org_id:org.id,amount:parseFloat(fields.amount)||0,amount_type:fields.amountType||"per_period",currency:fields.currency||currencyForCountry(org?.country),frequency:fields.frequency,created_by:auth.coach.id}]);
    if(!created||!created[0])return;
    const planRow=created[0];
    const n=fields.numPeriods;
    const start=fields.startDate?new Date(fields.startDate+"T00:00:00"):new Date();
    const toCreate=[];
    for(let i=0;i<n;i++){
      let dueDate=null;
      if(fields.frequency!=="custom"){
        const d=new Date(start);
        if(fields.frequency==="weekly")d.setDate(d.getDate()+7*i);
        else if(fields.frequency==="monthly")d.setMonth(d.getMonth()+i);
        dueDate=toISODate(d);
      }
      toCreate.push({plan_id:planRow.id,period_index:i,due_date:dueDate});
    }
    const createdPeriods=await api.post(auth.token,"payment_periods",toCreate);
    setPlan(planRow);
    setPeriods(createdPeriods||[]);
    setRecords([]);
  }

  async function addPeriod(){
    if(!plan)return;
    const last=periods[periods.length-1];
    let d=last?.due_date?new Date(last.due_date+"T00:00:00"):new Date();
    if(plan.frequency==="weekly")d.setDate(d.getDate()+7);
    else if(plan.frequency==="monthly")d.setMonth(d.getMonth()+1);
    const created=await api.post(auth.token,"payment_periods",[{plan_id:plan.id,period_index:periods.length,due_date:toISODate(d)}]);
    if(created&&created[0])setPeriods(prev=>sortPeriods([...prev,created[0]]));
  }

  async function deletePeriod(periodId){
    await api.del(auth.token,"payment_periods",periodId);
    setPeriods(prev=>prev.filter(p=>p.id!==periodId));
    setRecords(prev=>prev.filter(r=>r.period_id!==periodId));
  }

  async function updatePeriodDate(periodId,dateStr){
    setPeriods(prev=>sortPeriods(prev.map(p=>p.id===periodId?{...p,due_date:dateStr}:p)));
    await api.patch(auth.token,"payment_periods",periodId,{due_date:dateStr});
  }

  // Both label and amount are optional per-period overrides — used together
  // they let a coach slot in a one-off required payment (e.g. a tournament
  // entry fee) alongside the team's regular recurring schedule, without it
  // needing to match the plan's usual amount or being stuck with a generic
  // "Period N" name. Blank/empty means "use the plan's normal amount" and
  // "just call it Period N", exactly like before this existed.
  async function updatePeriodLabel(periodId,label){
    const val=label.trim()||null;
    await api.patch(auth.token,"payment_periods",periodId,{label:val});
    setPeriods(prev=>prev.map(p=>p.id===periodId?{...p,label:val}:p));
  }
  async function updatePeriodAmount(periodId,amountStr){
    const val=amountStr===""?null:parseFloat(amountStr);
    const clean=val===null||isNaN(val)?null:val;
    await api.patch(auth.token,"payment_periods",periodId,{amount:clean});
    setPeriods(prev=>prev.map(p=>p.id===periodId?{...p,amount:clean}:p));
  }

  async function saveAmount(){
    const val=parseFloat(amountDraft);
    if(isNaN(val)||!plan)return;
    await api.patch(auth.token,"payment_plans",plan.id,{amount:val,amount_type:amountTypeDraft,currency:currencyDraft});
    setPlan(p=>({...p,amount:val,amount_type:amountTypeDraft,currency:currencyDraft}));
    setEditingAmount(false);
  }

  async function deletePlanEntirely(){
    if(!plan)return;
    await api.del(auth.token,"payment_plans",plan.id);
    setPlan(null);setPeriods([]);setRecords([]);setShowDeleteConfirm(false);
  }

  async function toggleVisibility(field,val){
    if(!plan)return;
    setPlan(p=>({...p,[field]:val}));
    await api.patch(auth.token,"payment_plans",plan.id,{[field]:val});
  }

  function exportPayments(){
    if(!plan)return;
    const sym=currencySymbol(plan.currency);
    const header=["Player",...periods.map((p,i)=>`${p.label||`Period ${i+1}`} (${p.due_date?fmtDateShort(p.due_date):"no date"}) [${sym}${periodAmount(p).toFixed(2)}]`)];
    const rows=teamPlayers.map(pl=>[pl.name,...periods.map(per=>{
      const rec=records.find(r=>r.period_id===per.id&&r.player_id===pl.id);
      return statusInfo(rec?rec.status:"not_due").label;
    })]);
    downloadCSV(`${(activeTeam?.name||"team").replace(/[^a-z0-9]+/gi,"_")}-payments.csv`,[header,...rows]);
  }

  async function cycleStatus(periodId,playerId){
    const order=PAYMENT_STATUSES.map(s=>s.key);
    const rec=records.find(r=>r.period_id===periodId&&r.player_id===playerId);
    const cur=rec?rec.status:"not_due";
    // Paid-via-Stripe records are locked against accidental clicks — cycling through
    // statuses by mis-tapping a row would silently disagree with a real charge that
    // actually happened. A coach can still override it (e.g. after refunding a parent
    // outside the app), but has to confirm first rather than it just being one more
    // click in the usual cycle.
    if(rec?.paid_via==="stripe"&&!window.confirm("This payment was paid online via Stripe. Changing its status here doesn't refund the parent or cancel the charge — it only changes what pitchMGR shows. Override it anyway?"))return;
    const next=order[(order.indexOf(cur)+1)%order.length];
    if(rec){
      setRecords(prev=>prev.map(r=>r.id===rec.id?{...r,status:next}:r));
      await api.patch(auth.token,"payment_records",rec.id,{status:next,updated_at:new Date().toISOString()});
    } else {
      const created=await api.post(auth.token,"payment_records",[{period_id:periodId,player_id:playerId,status:next}]);
      if(created&&created[0])setRecords(prev=>[...prev,created[0]]);
    }
  }

  // Sends a polite reminder notification for one overdue payment, to whichever of the
  // player's linked parent account(s) and/or their own linked player account the
  // coach has made this plan visible to (never to someone who can't even see the
  // payment). Reuses the existing "new_message" notification category — the app only
  // ships four notification categories today, each with its own opt-out toggle in
  // Settings, and "New Message" is the closest existing fit for a direct reminder like
  // this; add a dedicated "Payment Reminders" category later if finer control is needed.
  const [remindingKey,setRemindingKey]=useState(null); // `${periodId}:${playerId}` while sending
  const [reminderResult,setReminderResult]=useState({}); // `${periodId}:${playerId}` -> {ok,message} — shown under the button, then fades
  async function sendPaymentReminder(pl,per){
    if(!plan)return;
    const key=`${per.id}:${pl.id}`;
    setRemindingKey(key);
    setReminderResult(prev=>{const n={...prev};delete n[key];return n;});
    try{
      const ids=new Set();
      if(plan.visible_to_players){
        const playerViewers=await api.get(auth.token,"team_viewers",`player_id=eq.${pl.id}&account_role=eq.player&select=id`);
        (playerViewers||[]).forEach(v=>ids.add(v.id));
      }
      if(plan.visible_to_parents){
        const parentLinks=await api.get(auth.token,"parent_children",`player_id=eq.${pl.id}&select=parent_viewer_id`);
        (parentLinks||[]).forEach(p=>ids.add(p.parent_viewer_id));
      }
      if(ids.size===0){
        setReminderResult(prev=>({...prev,[key]:{ok:false,message:"No linked parent/player account to notify yet"}}));
        return;
      }
      const sym=currencySymbol(plan.currency);
      const amt=periodAmount(per);
      const dueStr=per.due_date?fmtDateShort(per.due_date):"the due date";
      const res=await sendPushNotification(auth.token,[...ids],"new_message","Payment Reminder 💷",`Hi — a friendly reminder that ${pl.name}'s payment of ${sym}${amt.toFixed(2)} due ${dueStr} is now overdue. Please arrange payment as soon as possible when you get a chance. Thank you!`);
      if(res&&res.ok){
        setReminderResult(prev=>({...prev,[key]:{ok:true,message:`✓ Sent to ${res.count}`,ts:Date.now()}}));
      } else {
        setReminderResult(prev=>({...prev,[key]:{ok:false,message:"Failed to send — try again"}}));
      }
    } finally { setRemindingKey(null); }
  }

  if(!activeTeamId){
    return(
      <div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:760}}>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase"}}>💷 Payments — Select a Team</div>
        {teams.length===0?(
          <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)"}}>
            <div style={{fontSize:28,marginBottom:8}}>💷</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--c-muted)"}}>No teams yet</div>
            <div style={{fontSize:11,marginTop:4}}>Create a team first, then come back here to set up payments for it.</div>
          </div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)",gap:9}}>
            {teams.map(t=>(
              <button key={t.id} onClick={()=>setActiveTeamId(t.id)} style={{...C,padding:14,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:9}}>
                <span style={{fontSize:18}}>{t.badge}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13}}>{t.name}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)"}}>{(t.playerIds||[]).length} player{(t.playerIds||[]).length===1?"":"s"}</div>
                </div>
                <span style={{fontSize:12,color:"var(--c-muted2)"}}>→</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <button onClick={()=>{setActiveTeamId(null);setPlan(undefined);}} style={{alignSelf:"flex-start",background:"none",border:"none",color:ac,fontSize:11,fontWeight:700,cursor:"pointer",padding:0}}>← All Teams</button>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:18}}>{activeTeam?.badge}</span>
        <div style={{fontSize:16,fontWeight:800}}>{activeTeam?.name} · Payments</div>
      </div>

      {plan===undefined?(
        <div style={{padding:30,textAlign:"center",color:"var(--c-muted)"}}>Loading…</div>
      ):plan===null?(
        <PaymentPlanSetup ac={ac} AS={AS} orgCountry={org?.country} onCreate={createPlan}/>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {!org?.stripe_charges_enabled&&(
            <div style={{...C,padding:"11px 14px",background:"#3b82f622",border:"1px solid #3b82f655",fontSize:11,color:"var(--c-text2)"}}>
              💳 Parents currently have to be marked as paid by hand. Connect a bank account from <b>Settings → Payouts</b> to let them pay online instead — pitchMGR takes a small platform fee and the rest goes straight to your account.
            </div>
          )}
          <div style={{...C,padding:14,display:"flex",alignItems:"center",flexWrap:"wrap",gap:14}}>
            <div>
              <div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Amount</div>
              {editingAmount?(
                <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:3}}>
                  <select value={currencyDraft} onChange={e=>setCurrencyDraft(e.target.value)} style={{padding:"4px 6px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:10}}>
                    {CURRENCIES.map(c=><option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                  <div style={{display:"flex",gap:5}}>
                    {PAYMENT_AMOUNT_TYPES.map(t=>(
                      <button key={t.key} onClick={()=>setAmountTypeDraft(t.key)} style={{padding:"4px 8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:9,fontWeight:700,background:amountTypeDraft===t.key?ac:AS.bgColor,color:amountTypeDraft===t.key?"#fff":"var(--c-muted)"}}>{t.label}</button>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <input type="number" min="0" step="0.01" value={amountDraft} onChange={e=>setAmountDraft(e.target.value)} style={{width:80,padding:"5px 7px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:12}}/>
                    <button onClick={saveAmount} style={{padding:"5px 10px",borderRadius:6,border:"none",background:ac,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Save</button>
                    <button onClick={()=>setEditingAmount(false)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
                  </div>
                </div>
              ):(
                <div>
                  <div style={{fontSize:15,fontWeight:800,marginTop:2}}>{currencySymbol(plan.currency)}{Number(plan.amount||0).toFixed(2)} <span style={{fontSize:10,fontWeight:600,color:"var(--c-muted)"}}>{plan.amount_type==="total"?"total":"per payment"} · {plan.currency||"GBP"}</span> <button onClick={()=>{setAmountDraft(String(plan.amount||""));setAmountTypeDraft(plan.amount_type||"per_period");setCurrencyDraft(plan.currency||currencyForCountry(org?.country));setEditingAmount(true);}} style={{background:"none",border:"none",color:ac,cursor:"pointer",fontSize:10,fontWeight:700}}>✎ Edit</button></div>
                  {plan.amount_type==="total"&&periods.length>0&&<div style={{fontSize:10,color:"var(--c-muted2)",marginTop:2}}>≈ {currencySymbol(plan.currency)}{perInstallmentAmount(plan,periods.length).toFixed(2)} per payment across {periods.length} payment{periods.length!==1?"s":""}</div>}
                </div>
              )}
            </div>
            <div>
              <div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Frequency</div>
              <div style={{fontSize:15,fontWeight:800,marginTop:2}}>{freqLabel(plan.frequency)}</div>
            </div>
            <div style={{marginLeft:"auto"}}>
              {!showDeleteConfirm?(
                <button onClick={()=>setShowDeleteConfirm(true)} style={{padding:"7px 12px",borderRadius:7,border:"1px solid #ef4444",background:"none",color:"#ef4444",fontWeight:700,fontSize:11,cursor:"pointer"}}>Delete Plan</button>
              ):(
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:11,color:"#ef4444",fontWeight:600}}>Delete plan &amp; all records?</span>
                  <button onClick={deletePlanEntirely} style={{padding:"7px 12px",borderRadius:7,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Yes, Delete</button>
                  <button onClick={()=>setShowDeleteConfirm(false)} style={{padding:"7px 12px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
                </div>
              )}
            </div>
          </div>

          <div style={{...C,padding:14,display:"flex",flexWrap:"wrap",gap:20,alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:9}}>
              <div>
                <div style={{fontSize:12,fontWeight:700}}>👪 Visible to Parents</div>
                <div style={{fontSize:10,color:"var(--c-muted)",marginTop:1}}>Parents see this player's own payment status only.</div>
              </div>
              <button onClick={()=>toggleVisibility("visible_to_parents",!plan.visible_to_parents)} style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background:plan.visible_to_parents?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
                <span style={{position:"absolute",top:2,left:plan.visible_to_parents?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
              </button>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:9}}>
              <div>
                <div style={{fontSize:12,fontWeight:700}}>🧑 Visible to Players</div>
                <div style={{fontSize:10,color:"var(--c-muted)",marginTop:1}}>Players see their own payment status only.</div>
              </div>
              <button onClick={()=>toggleVisibility("visible_to_players",!plan.visible_to_players)} style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background:plan.visible_to_players?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
                <span style={{position:"absolute",top:2,left:plan.visible_to_players?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
              </button>
            </div>
            <button onClick={exportPayments} style={{marginLeft:"auto",padding:"8px 14px",borderRadius:7,border:`1px solid ${ac}`,background:"none",color:ac,fontWeight:700,fontSize:11,cursor:"pointer"}}>⬇ Export as Spreadsheet (CSV)</button>
          </div>

          <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
            <div style={{fontSize:10,color:"var(--c-muted)",fontWeight:700}}>Click a cell to cycle status:</div>
            {PAYMENT_STATUSES.map(s=>(
              <div key={s.key} style={{display:"flex",alignItems:"center",gap:5}}>
                <span style={{width:10,height:10,borderRadius:3,background:s.color,display:"inline-block"}}/>
                <span style={{fontSize:10,color:"var(--c-muted)"}}>{s.label}</span>
              </div>
            ))}
          </div>

          <div style={{...C,overflow:"hidden"}}>
            <div ref={periodsScrollRef} style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${AS.borderColor}`}}>
                    <th style={{padding:"9px 10px",textAlign:"left",fontSize:9,color:"var(--c-text)",textTransform:"uppercase",position:"sticky",left:0,background:AS.surfaceColor,borderRight:hasHiddenLeft?`1px solid ${AS.borderColor}`:"none"}}>Player{hasHiddenLeft&&<span style={{marginLeft:6,color:ac,fontWeight:700}} title="More periods — scroll to see them">⋯</span>}</th>
                    {periods.map((p,i)=>(
                      <th key={p.id} style={{padding:"9px 8px",fontSize:9,color:"var(--c-text)",textTransform:"uppercase",minWidth:132}}>
                        <input value={p.label||""} onChange={e=>setPeriods(prev=>prev.map(pp=>pp.id===p.id?{...pp,label:e.target.value}:pp))} onBlur={e=>updatePeriodLabel(p.id,e.target.value)} placeholder={`Period ${i+1}`} title="Optional label — e.g. 'Tournament Fee' for a one-off payment" style={{width:"100%",padding:"3px 4px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontWeight:700,fontSize:10,marginBottom:3,textTransform:"none"}}/>
                        <input type="date" value={p.due_date||""} onChange={e=>updatePeriodDate(p.id,e.target.value)} style={{width:"100%",padding:"3px 4px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text2)",fontSize:10,marginBottom:3}}/>
                        <input type="number" min="0" step="0.01" value={p.amount??""} onChange={e=>setPeriods(prev=>prev.map(pp=>pp.id===p.id?{...pp,amount:e.target.value}:pp))} onBlur={e=>updatePeriodAmount(p.id,e.target.value)} placeholder={`${currencySymbol(plan.currency)}${periodAmount(p).toFixed(2)} (default)`} title="Optional — overrides this period's amount only, e.g. a bigger one-off tournament fee" style={{width:"100%",padding:"3px 4px",borderRadius:5,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text2)",fontSize:10,marginBottom:3,textTransform:"none"}}/>
                        {periods.length>1&&<button onClick={()=>deletePeriod(p.id)} title="Remove this period" style={{background:"none",border:"none",color:"var(--c-muted2)",cursor:"pointer",fontSize:9}}>✕ remove</button>}
                      </th>
                    ))}
                    <th style={{padding:"9px 8px"}}>
                      <button onClick={addPeriod} title="Add another period" style={{padding:"6px 10px",borderRadius:6,border:`1px dashed ${ac}`,background:"none",color:ac,fontWeight:700,fontSize:10,cursor:"pointer",whiteSpace:"nowrap"}}>+ Add Period</button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamPlayers.length===0?(
                    <tr><td colSpan={periods.length+2} style={{padding:24,textAlign:"center",color:"var(--c-muted2)"}}>No players on this team yet.</td></tr>
                  ):teamPlayers.map(pl=>(
                    <tr key={pl.id} style={{borderBottom:`1px solid ${AS.borderColor}`}}>
                      <td style={{padding:"8px 10px",fontWeight:600,whiteSpace:"nowrap",position:"sticky",left:0,background:AS.surfaceColor,borderRight:hasHiddenLeft?`1px solid ${AS.borderColor}`:"none"}}><Flag value={pl.nationality}/> {pl.name}</td>
                      {periods.map(per=>{
                        const rec=records.find(r=>r.period_id===per.id&&r.player_id===pl.id);
                        const s=statusInfo(rec?rec.status:"not_due");
                        const key=`${per.id}:${pl.id}`;
                        const isOverdue=(rec?rec.status:"not_due")==="overdue";
                        const canNotify=plan&&(plan.visible_to_parents||plan.visible_to_players);
                        const result=reminderResult[key];
                        return(
                          <td key={per.id} style={{padding:"6px 8px",textAlign:"center"}}>
                            <button onClick={()=>cycleStatus(per.id,pl.id)} title={rec?.paid_via==="stripe"?"Paid online via Stripe":undefined} style={{width:"100%",padding:"7px 4px",borderRadius:7,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:s.color+"22",color:s.color}}>{s.label}{rec?.paid_via==="stripe"?" 💳":""}</button>
                            {isOverdue&&canNotify&&(<>
                              <button onClick={()=>sendPaymentReminder(pl,per)} disabled={remindingKey===key} title="Send a payment reminder notification" style={{marginTop:4,width:"100%",padding:"5px 4px",borderRadius:6,border:`1px solid ${s.color}55`,background:"none",cursor:"pointer",fontSize:9,fontWeight:700,color:s.color,opacity:remindingKey===key?0.6:1}}>
                                {remindingKey===key?"Sending…":result?.ok?"🔔 Remind again":"🔔 Remind"}
                              </button>
                              {result&&(
                                <div style={{marginTop:3,fontSize:9,fontWeight:700,color:result.ok?"#22c55e":"#ef4444"}}>{result.message}</div>
                              )}
                            </>)}
                          </td>
                        );
                      })}
                      <td/>
                    </tr>
                  ))}
                  {teamPlayers.length>0&&(()=>{
                    const sym=currencySymbol(plan.currency);
                    return(
                      <tr style={{borderTop:`2px solid ${AS.borderColor}`}}>
                        <td style={{padding:"8px 10px",fontWeight:800,fontSize:11,whiteSpace:"nowrap",position:"sticky",left:0,background:AS.surfaceColor,borderRight:hasHiddenLeft?`1px solid ${AS.borderColor}`:"none"}}>💰 Collected</td>
                        {periods.map(per=>{
                          const paidCount=teamPlayers.filter(pl=>{
                            const rec=records.find(r=>r.period_id===per.id&&r.player_id===pl.id);
                            return rec?.status==="paid";
                          }).length;
                          const total=paidCount*periodAmount(per);
                          return(
                            <td key={per.id} style={{padding:"8px 8px",textAlign:"center"}}>
                              <div style={{fontWeight:800,fontSize:12,color:"#22c55e"}}>{sym}{total.toFixed(2)}</div>
                              <div style={{fontSize:9,color:"var(--c-muted2)",marginTop:1}}>{paidCount} of {teamPlayers.length} paid</div>
                            </td>
                          );
                        })}
                        <td/>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Payment status view for a single player (used by both ParentApp and PlayerApp) —
// shows only that player's own record for each period, never the rest of the team's.

export function MyPaymentsView({teamId,playerId,token,ac,AS,org,accountRole}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [plan,setPlan]=useState(undefined); // undefined=loading, null=none visible
  const [periods,setPeriods]=useState([]);
  const [records,setRecords]=useState([]);
  const [payingKey,setPayingKey]=useState(null); // period.id while a checkout redirect is starting
  const [payError,setPayError]=useState("");
  const canPayOnline=!!(org?.stripe_charges_enabled&&accountRole&&teamId&&playerId);

  useEffect(()=>{ if(teamId&&playerId)load(); else setPlan(null); },[teamId,playerId]);

  // A parent/player lands back here from Stripe Checkout with ?payment=success or
  // ?payment=cancelled in the URL. The webhook that actually marks the record paid
  // can take a few seconds to arrive, so on a successful return this reloads once
  // immediately (in case it already landed) and once more after a short delay,
  // rather than leaving the page showing a stale "not paid" status.
  const [returnBanner,setReturnBanner]=useState(null);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const outcome=params.get("payment");
    if(!outcome)return;
    window.history.replaceState({},"",window.location.pathname);
    if(outcome==="success"){
      setReturnBanner({ok:true,message:"✓ Payment received — it may take a few seconds to show as paid below."});
      if(teamId&&playerId){ load(); setTimeout(load,4000); }
    } else if(outcome==="cancelled"){
      setReturnBanner({ok:false,message:"Payment cancelled — nothing was charged."});
    }
    setTimeout(()=>setReturnBanner(null),8000);
  },[]);

  async function load(){
    setPlan(undefined);
    const rows=await api.get(token,"payment_plans",`team_id=eq.${teamId}`);
    const p=rows&&rows[0]?rows[0]:null;
    setPlan(p);
    if(p){
      const pers=await api.get(token,"payment_periods",`plan_id=eq.${p.id}&order=period_index.asc`);
      setPeriods(sortPeriods(pers));
      const perIds=(pers||[]).map(x=>x.id);
      if(perIds.length>0){
        const recs=await api.get(token,"payment_records",`period_id=in.(${perIds.join(",")})&player_id=eq.${playerId}`);
        setRecords(recs||[]);
      } else setRecords([]);
    } else { setPeriods([]);setRecords([]); }
  }

  async function payNow(per){
    setPayingKey(per.id);setPayError("");
    const res=await createPaymentCheckout(token,{periodId:per.id,playerId,accountRole});
    if(res.ok&&res.url){ window.location.href=res.url; return; }
    setPayError(res.error||"Couldn't start the payment — please try again.");
    setPayingKey(null);
  }

  if(plan===undefined)return <div style={{padding:30,textAlign:"center",color:"var(--c-muted)"}}>Loading…</div>;
  if(!plan)return <div style={{...C,padding:30,textAlign:"center",color:"var(--c-muted2)"}}>No payment info available for this team yet.</div>;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {returnBanner&&(
        <div style={{...C,padding:"10px 14px",fontSize:12,fontWeight:700,color:returnBanner.ok?"#22c55e":"#ef4444"}}>{returnBanner.message}</div>
      )}
      <div style={{...C,padding:14,display:"flex",gap:20,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Amount</div>
          <div style={{fontSize:15,fontWeight:800,marginTop:2}}>{currencySymbol(plan.currency)}{Number(plan.amount||0).toFixed(2)} <span style={{fontSize:10,fontWeight:600,color:"var(--c-muted)"}}>{plan.amount_type==="total"?"total":"per payment"}</span></div>
          {plan.amount_type==="total"&&periods.length>0&&<div style={{fontSize:10,color:"var(--c-muted2)",marginTop:2}}>≈ {currencySymbol(plan.currency)}{perInstallmentAmount(plan,periods.length).toFixed(2)} per payment</div>}
        </div>
        <div>
          <div style={{fontSize:9,color:"var(--c-text)",textTransform:"uppercase",fontWeight:700}}>Frequency</div>
          <div style={{fontSize:15,fontWeight:800,marginTop:2}}>{freqLabel(plan.frequency)}</div>
        </div>
      </div>
      <div style={{...C,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${AS.borderColor}`}}>
              <th style={{padding:"9px 12px",textAlign:"left",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>Period</th>
              <th style={{padding:"9px 12px",textAlign:"left",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>Due Date</th>
              <th style={{padding:"9px 12px",textAlign:"left",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>Amount</th>
              <th style={{padding:"9px 12px",textAlign:"right",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>Status</th>
            </tr>
          </thead>
          <tbody>
            {periods.length===0?(
              <tr><td colSpan={4} style={{padding:24,textAlign:"center",color:"var(--c-muted2)"}}>No periods set up yet.</td></tr>
            ):periods.map((per,i)=>{
              const rec=records.find(r=>r.period_id===per.id);
              const status=rec?rec.status:"not_due";
              const s=statusInfo(status);
              const payable=canPayOnline&&status!=="paid"&&status!=="exempt";
              const amt=per.amount!=null?Number(per.amount):(plan.amount_type==="total"?perInstallmentAmount(plan,periods.length):Number(plan.amount||0));
              return(
                <tr key={per.id} style={{borderBottom:`1px solid ${AS.bgColor}`}}>
                  <td style={{padding:"9px 12px",fontWeight:600}}>{per.label||`Period ${i+1}`}</td>
                  <td style={{padding:"9px 12px",color:"var(--c-muted)"}}>{per.due_date?fmtDateShort(per.due_date):"—"}</td>
                  <td style={{padding:"9px 12px",color:"var(--c-muted)"}}>{currencySymbol(plan.currency)}{amt.toFixed(2)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right"}}>
                    <span style={{padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:700,background:s.color+"22",color:s.color}}>{s.label}{rec?.paid_via==="stripe"?" 💳":""}</span>
                    {payable&&(
                      <div style={{marginTop:5}}>
                        <button onClick={()=>payNow(per)} disabled={payingKey===per.id} style={{padding:"6px 12px",borderRadius:6,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:10,cursor:payingKey===per.id?"default":"pointer",opacity:payingKey===per.id?0.7:1}}>
                          {payingKey===per.id?"Redirecting…":"💳 Pay Now"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {payError&&<div style={{fontSize:11,color:"#ef4444",textAlign:"center"}}>{payError}</div>}
      {canPayOnline&&<div style={{fontSize:10,color:"var(--c-muted2)",textAlign:"center"}}>Payments are processed securely by Stripe — pitchMGR never sees or stores your card details.</div>}
    </div>
  );
}

